import React, { type FC, useMemo, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Drawer, Form } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { UsersAvatarBlock, DrawerFooterBlock, CloseDrawerButton } from '../../shared'
import { operationDrawerProps, showNotification } from '../../../constants/helpers/common'
import { usePutRelationshipsMutation, usePostRelationshipsMutation } from '../../../state/apis/generated/relationshipsApi'
import { selectNamespace } from '../../../state/slices/appSlice'

import {
  type IUserPermission,
  type IDrawerFooterBlock,
  type ICreateEditPanelState
} from '../../../constants/types/common'
import { IOktaUser } from '../../../constants/types/generatedTypesAliases'
import { AddUserPermissionsForm } from './AddUserPermissionsForm'

dayjs.extend(utc)

interface IUserPermissionFormValues extends Omit<IUserPermission, 'expiresAt'> {
  expiresAt?: Dayjs | null
}

interface ICreateOrEditUserPermissionsPanelProps {
  selectedUsers: IOktaUser[]
  createEditPanelState: ICreateEditPanelState
  setCreateEditPanelState: (state: ICreateEditPanelState) => void
  closable?: boolean
}

export const CreateOrEditUserPermissionsPanel: FC<ICreateOrEditUserPermissionsPanelProps> = ({
  closable,
  selectedUsers,
  createEditPanelState,
  setCreateEditPanelState,
}) => {
  const mode = createEditPanelState?.operation || 'create'
  const namespaceSelector = useSelector(selectNamespace)
  const [form] = Form.useForm<IUserPermissionFormValues>()
  const values = Form.useWatch([], form)
  const [addPermission, { isLoading: isCreateRelationshipLoading }] = usePostRelationshipsMutation()
  const [editPermission] = usePutRelationshipsMutation()

  const labelsMap = useMemo(() => ({
    create: {
      panelTitle: 'Add a Permission',
      confirmButtonLabel: 'Add Permission',
      successMessage: `Permission added to ${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}`,
    },
    edit: {
      panelTitle: 'Edit Permission',
      confirmButtonLabel: 'Save Changes',
      successMessage: 'Permission saved',
    }
  }), [selectedUsers])

  useEffect(() => {
    if (mode === 'edit' && createEditPanelState?.payload) {
      const payload = createEditPanelState.payload
      form.setFieldsValue({
        ...payload,
        expiresAt: payload.expiresAt ? dayjs(payload.expiresAt) : null
      })
    } else {
      form.setFieldValue('namespace', namespaceSelector)
    }
  }, [mode, form, createEditPanelState?.payload, namespaceSelector])

  const isConfirmDisabled = useMemo(() => {
    if (!values) return true
    const requiredFields = ['namespace', 'resourceType', 'resourceId', 'relation'] as const
    return requiredFields.some(field => !values[field])
  }, [values])

  const handleClosePanel = useCallback(() => {
    form.resetFields()
    mode !== 'edit' && form.setFieldValue('namespace', namespaceSelector)
    setCreateEditPanelState(null)
  }, [form, setCreateEditPanelState, namespaceSelector, mode])

  const handleConfirmPermission = useCallback(async () => {
    const selectedUserId = selectedUsers[0].id
    const permission = form.getFieldsValue()
    const initialPermission = createEditPanelState?.payload as IUserPermission
    await form.validateFields()

    // Convert expiresAt to UTC ISO string if present
    const expiresAtUtc = permission.expiresAt
      ? dayjs(permission.expiresAt).utc().startOf('day').toISOString()
      : null

    let serverErrors
    const localErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length)

    if (!localErrors) {
      if (mode === 'edit') {
        serverErrors = await editPermission({
          serviceAuthorisationDomainModelsSpiceDbRequestsSpiceDbUpdateRelationshipsRequest: {
            relationshipsToAdd: [{
              userId: selectedUserId,
              namespace: permission.namespace,
              resourceType: `${permission.namespace}/${permission.resourceType}`,
              resourceId: permission.resourceId,
              relation: permission.relation,
              expiresAt: expiresAtUtc
            }],
            relationshipsToDelete: [{
              userId: selectedUserId,
              namespace: initialPermission.namespace,
              resourceType: `${initialPermission.namespace}/${initialPermission.resourceType}`,
              resourceId: initialPermission.resourceId,
              relation: initialPermission.relation,
              expiresAt: initialPermission.expiresAt ?? null
            }]
          }
        })
      } else {
        const userIds = selectedUsers.map(u => u.id)
        const { namespace, resourceType, relation, resourceId } = permission
        const relationships = userIds.map(userId => ({
          userId,
          namespace,
          resourceId,
          resourceType: `${namespace}/${resourceType}`,
          relation,
          expiresAt: expiresAtUtc
        }))
        serverErrors = await addPermission({
          serviceAuthorisationDomainModelsSpiceDbRequestsSpiceDbWriteRelationshipsRequest: {
            relationships
          }
        })
      }
      showNotification({ result: serverErrors, success: labelsMap[mode].successMessage })
      handleClosePanel()
    }
  }, [
    mode,
    form,
    labelsMap,
    selectedUsers,
    createEditPanelState?.payload,
    addPermission,
    editPermission,
    handleClosePanel,
  ])

  const footerButtonsProps: IDrawerFooterBlock = useMemo(() => ({
    confirm: {
      children: labelsMap[mode].confirmButtonLabel,
      disabled: isConfirmDisabled,
      loading: isCreateRelationshipLoading,
      onClick: handleConfirmPermission
    },
    cancel: {
      onClick: handleClosePanel
    },
    tooltipText: isConfirmDisabled ? 'All fields are required.' : ''
  }), [
    mode,
    labelsMap,
    isConfirmDisabled,
    isCreateRelationshipLoading,
    handleConfirmPermission,
    handleClosePanel
  ])

  return (
    <Drawer
      footer={
        <DrawerFooterBlock {...footerButtonsProps} />
      }
      extra={(
        <CloseDrawerButton onCloseDrawer={handleClosePanel} />
      )}
      {...operationDrawerProps}
      {...typeof closable === 'boolean' ? { closable } : {}}
      title={labelsMap[mode].panelTitle}
      data-testid='create-edit-permission-drawer'
      onClose={handleClosePanel}
      open={Boolean(createEditPanelState)}>
      <UsersAvatarBlock users={selectedUsers} />
      <AddUserPermissionsForm form={form} mode={mode} />
    </Drawer>
  )
}
